import { firstColor, gradientColors } from '@/lib/utils';

/**
 * Resolución del degradado de una flip card (FLIPCARD_SPEC_v3 §5).
 *
 * Vive aquí, en el frontend y en un solo archivo, a propósito: es la misma
 * lógica que necesita el render del miembro y la vista previa en vivo del
 * admin (que corre sobre valores que todavía no se guardan). Duplicarla en
 * PHP habría creado dos verdades que se desincronizan.
 */

const NARANJA_MARCA = '#FA4D0A';

/** Ángulo de marca. Todo degradado de VONDI nace a 100°. */
export const ANGULO_MARCA = 100;

/* Destinos de los degradados de marca, en HSL.
   ROSA  = el final de --grad-rojo-rosa (#FA759F): a donde van las rojas.
   CORAL = el final de --grad-marca (#F73E48): a donde van las naranjas. */
const ROSA = { h: 341, s: 0.93, l: 0.72 };
const CORAL = { h: 357, s: 0.92, l: 0.606 };

function hexToHsl(hex) {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: h * 360, s, l };
}

function hslToHex({ h: hRaw, s, l }) {
  const h = ((hRaw % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60 ? [c, x, 0]
    : h < 120 ? [x, c, 0]
    : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c]
    : h < 300 ? [x, 0, c]
    : [c, 0, x];
  const to = (n) =>
    Math.max(0, Math.min(255, Math.round((n + m) * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/* El tono es circular: para interpolar alrededor del rojo (0°) hay que tratar
   los tonos altos como negativos, si no un 357° y un 3° se cruzarían por el
   lado largo del círculo pasando por el verde. */
const continuo = (h) => (h > 180 ? h - 360 : h);

function mezclarHacia(desde, hacia, t) {
  const hDesde = continuo(desde.h);
  const hHacia = continuo(hacia.h);
  return hslToHex({
    h: hDesde + (hHacia - hDesde) * t,
    s: desde.s + (hacia.s - desde.s) * t,
    l: desde.l + (hacia.l - desde.l) * t,
  });
}

/**
 * Deriva el color final cuando el admin solo definió el de inicio
 * (FLIPCARD_SPEC §5: "si solo se define color_inicio, generar el color_fin").
 *
 * Se resuelve por familias de tono, siguiendo los degradados que el manual ya
 * define en vez de inventar una regla propia:
 *
 *   - **Rojos → rosa.** Es la única regla que el spec enuncia explícitamente
 *     ("en las cards rojas, el degradado va hacia el rosa"). Destino: el final
 *     de --grad-rojo-rosa.
 *   - **Naranjas → coral.** Es literalmente --grad-marca (#FA4D0A → #F73E48).
 *   - **Todo lo demás → el mismo tono, más claro.** Es lo que hacen las
 *     muestras del manual en verde, lavanda y ámbar.
 *
 * Las bandas de tono son estrechas a propósito: con una banda cálida ancha, el
 * ámbar (33°) caía en la regla del coral y una tarjeta ámbar terminaba
 * degradando a rojo, que no es lo que muestra el manual.
 */
export function derivarColorFin(inicio) {
  const hsl = hexToHsl(inicio);
  if (!hsl) return inicio;

  const h = continuo(hsl.h);

  // Rojo saturado y de luminosidad media o baja. Se pide que sea oscuro para
  // que un rosa pastel (ya rosa) no entre aquí y termine oscureciéndose.
  if (hsl.s > 0.5 && hsl.l < 0.62 && h >= -15 && h <= 10) {
    return mezclarHacia(hsl, ROSA, 0.85);
  }

  // Naranja de marca y vecinos.
  if (hsl.s > 0.4 && hsl.l < 0.7 && h > 10 && h <= 25) {
    return mezclarHacia(hsl, CORAL, 0.85);
  }

  return hslToHex({
    h: hsl.h + 6,
    s: Math.max(0, hsl.s - 0.05),
    l: Math.min(0.88, hsl.l + 0.16),
  });
}

/**
 * Devuelve el degradado efectivo de una tarjeta.
 *
 * Orden de precedencia:
 *   1. El color propio del beneficio (`colorStart` / `colorEnd`).
 *   2. El color de su tienda — es lo que se ve hoy, y lo que sigue viéndose
 *      mientras el admin no le asigne uno propio a la tarjeta.
 *   3. El naranja de marca.
 *
 * `storeColor` puede venir como "#a,#b" o "#a,#b,#c" (formato heredado de
 * `stores.color`), así que se aprovechan sus dos primeras paradas.
 *
 * @returns {{start: string, end: string, angle: number, css: string}}
 */
export function resolveGradient({ colorStart, colorEnd, gradientAngle, storeColor } = {}) {
  const heredados = storeColor ? gradientColors(storeColor, NARANJA_MARCA) : [];

  const start =
    colorStart || (storeColor ? firstColor(storeColor, NARANJA_MARCA) : null) || NARANJA_MARCA;

  const end = colorEnd || (!colorStart && heredados.length > 1 ? heredados[1] : null) || derivarColorFin(start);

  const angle = Number.isFinite(gradientAngle) ? gradientAngle : ANGULO_MARCA;

  return {
    start,
    end,
    angle,
    css: `linear-gradient(${angle}deg, ${start}, ${end})`,
  };
}
